import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Spin } from "antd";

import { Amplify } from "aws-amplify";
import { generateClient } from "aws-amplify/api";
import config from "./aws-exports";

import { onMessage, getToken } from "firebase/messaging";
import { messaging } from "./firebase/firebaseConfig";

import {
  MessageAction,
  NotificationAction,
  SidebarCountAction,
  CountriesAction,
} from "./store/actions";

import { listRooms } from "./graphql/queries";

import { onCreateRoom, onCreateMessageByRoomId } from "./graphql/subscriptions";

import DefaultNotification from "./components/notification/default-notification";

import showBrowserNotification from "./utils/showbrowsernotification";

import AllRoutes from "./config/routes/allroutes";
import images from "./assets";

Amplify.configure({
  API: {
    GraphQL: {
      endpoint:
        "https://eydejvaqhfglhpwghwdoqf3siq.appsync-api.ap-southeast-1.amazonaws.com/graphql",
      region: "ap-southeast-1",
      defaultAuthMode: "apiKey",
      apiKey: "da2-bixh7tnmprgrfm7evbh5vr6jpe",
    },
  },
});

const client = generateClient();

const requestPermission = async () => {
  const permission = await Notification.requestPermission();

  if (permission === "granted") {
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_VAPID_KEY,
    });
    localStorage.setItem("config_state_hash", JSON.stringify(token));
  } else if (permission === "denied") {
    console.error("You denied for the notification");
  }
};

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isLoading = useSelector((state) => state.auth.isLoading);
  const profileData = useSelector((state) => state?.profile?.profile);

  const [userList, setUserList] = useState([]);
  const [hasInteracted, setHasInteracted] = useState(false);

  const chimeSound = new Audio(
    "https://noble-marriage-public-bucket.s3.us-east-2.amazonaws.com/infographic-app-notification-twinkle-betacut-1-00-02.mp3",
  );

  useEffect(() => {
    const onUserInteraction = () => {
      setHasInteracted(true);
      window?.removeEventListener("click", onUserInteraction);
      window?.removeEventListener("keydown", onUserInteraction);
    };

    window?.addEventListener("click", onUserInteraction);
    window?.addEventListener("keydown", onUserInteraction);

    return () => {
      window?.removeEventListener("click", onUserInteraction);
      window?.removeEventListener("keydown", onUserInteraction);
    };
  }, []);

  useEffect(() => {
    requestPermission();
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith("/messages")) {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (profileData?.username) {
      getRoomList();
    }
  }, [profileData]);

  useEffect(() => {
    dispatch(CountriesAction.getCountries());
  }, [profileData]);

  useEffect(() => {
    if (profileData?.username) {
      const payload = {
        userList: userList,
        isLoading: false,
      };
      dispatch(MessageAction.setMessageList(payload));

      let unreadCount = 0;
      userList?.map((user) => {
        if (
          user.lastSenderId !== profileData.username &&
          user.lastMessageRead === false
        ) {
          unreadCount++;
        }
      });
      dispatch(MessageAction.setMessageSidebarCount(unreadCount));
    }
  }, [userList]);

  useEffect(() => {
    if (profileData?.username) {
      const subscription = client
        .graphql({
          query: onCreateRoom,
          variables: {},
        })
        .subscribe({
          next: (data) => {
            if (
              data?.data?.onCreateRoom?.receiverId === profileData?.username ||
              data?.data?.onCreateRoom?.senderId === profileData?.username
            ) {
              setUserList((prevList) => [
                data?.data?.onCreateRoom,
                ...prevList,
              ]);
            }
            if (
              data?.data?.onCreateRoom?.lastSenderId !==
                profileData?.username &&
              data?.data?.onCreateRoom?.firstMessageSent
            ) {
              chimeSound
                .play()
                .catch((err) => console.error("error playing audio", err));
              if (document.hidden) {
                showBrowserNotification(
                  "New message",
                  `You have a new message`,
                  images.notificationIcon,
                );
              }
              DefaultNotification(
                "New message",
                `You have a new message`,
                images.notificationIcon,
              );
            }
          },
        });
      return () => subscription.unsubscribe();
    }
  }, [profileData]);

  useEffect(() => {
    const msgsubscription = client
      .graphql({
        query: onCreateMessageByRoomId,
        variables: {},
      })
      .subscribe({
        next: async (data) => {
          const room = data?.data?.onCreateMessageByRoomId?.room;
          dispatch(SidebarCountAction.getSidebarCount());

          if (
            hasInteracted &&
            room?.lastSenderId &&
            room?.lastSenderId !== profileData?.username &&
            (room?.senderId === profileData?.username ||
              room?.receiverId === profileData?.username)
          ) {
            chimeSound
              .play()
              .catch((err) => console.error("error playing audio", err));
            if (document.hidden) {
              showBrowserNotification(
                "New message",
                `You have a new message`,
                images.notificationIcon,
              );
            }
            DefaultNotification(
              "New message",
              `You have a new message`,
              images.notificationIcon,
            );
            getRoomList();
          }
        },
      });
    return () => msgsubscription.unsubscribe();
  }, [profileData, hasInteracted]);

  onMessage(messaging, (payload) => {
    dispatch(NotificationAction.getNotifications());
    dispatch(SidebarCountAction.getSidebarCount());

    if (hasInteracted) {
      chimeSound
        .play()
        .catch((err) => console.error("error playing audio", err));
    }

    const notification =
      payload?.notification ||
      (payload?.data?.default && JSON.parse(payload.data.default));

    if (notification) {
      DefaultNotification(
        notification.title,
        notification.body,
        notification.image,
      );
    }
  });

  const getRoomList = async () => {
    try {
      const payload = {
        isLoading: true,
      };
      dispatch(
        MessageAction.setMessageList(payload, async () => {
          const result = await client.graphql({
            query: listRooms,
            variables: {
              searchId: profileData?.username,
            },
          });

          setUserList(result?.data?.listRooms?.items);
        }),
      );
    } catch (e) {
      console.error("in get Room list app js error", e);
    }
  };

  return (
    <div>
      <AllRoutes />
      <Spin spinning={isLoading} fullscreen />
    </div>
  );
};

export default App;
